import {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import {
  Plus,
  Search,
  X,
  Eye,
  Edit2,
  Copy,
  Trash2,
  Receipt,
  FileText,
  Paperclip,
  ExternalLink,
  Loader2,
} from "lucide-react";

import { toast } from "sonner";

import { Button } from "@/components/ui/button";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import TransactionForm from "@/components/TransactionForm";
import ExportBar from "@/components/ExportBar";
import { formatCurrency } from "@/lib/formatters";
import * as db from "@/lib/db";

const SORT_OPTIONS = [
  {
    value: "date_desc",
    label: "Newest first",
  },
  {
    value: "date_asc",
    label: "Oldest first",
  },
  {
    value: "amount_desc",
    label: "Highest amount",
  },
  {
    value: "amount_asc",
    label: "Lowest amount",
  },
];

const DEFAULT_FILTERS = {
  from: "",
  to: "",
  transactionTypeId: "all",
  categoryId: "all",
  taxTreatmentId: "all",
  sortBy: "date_desc",
  search: "",
};

const Transactions = () => {
  const [
    transactions,
    setTransactions,
  ] = useState([]);

  const [
    types,
    setTypes,
  ] = useState([]);

  const [
    categories,
    setCategories,
  ] = useState([]);

  const [
    taxTreatments,
    setTaxTreatments,
  ] = useState([]);

  const [
    events,
    setEvents,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    showForm,
    setShowForm,
  ] = useState(false);

  const [
    editing,
    setEditing,
  ] = useState(null);

  /*
   * ============================================================
   * VIEW TRANSACTION
   * ============================================================
   */

  const [
    viewTarget,
    setViewTarget,
  ] = useState(null);

  const [
    viewAttachments,
    setViewAttachments,
  ] = useState([]);

  const [
    loadingViewAttachments,
    setLoadingViewAttachments,
  ] = useState(false);

  const [
    openingAttachmentId,
    setOpeningAttachmentId,
  ] = useState(null);

  /*
   * ============================================================
   * DELETE TRANSACTION
   * ============================================================
   */

  const [
    deleteTarget,
    setDeleteTarget,
  ] = useState(null);

  const [
    linkedDeleteAsset,
    setLinkedDeleteAsset,
  ] = useState(null);

  const [
    checkingDeleteLink,
    setCheckingDeleteLink,
  ] = useState(false);

  const [
    deleting,
    setDeleting,
  ] = useState(false);

  /*
   * ============================================================
   * FILTERS
   * ============================================================
   */

  const [
    from,
    setFrom,
  ] = useState(
    DEFAULT_FILTERS.from
  );

  const [
    to,
    setTo,
  ] = useState(
    DEFAULT_FILTERS.to
  );

  const [
    transactionTypeId,
    setTransactionTypeId,
  ] = useState(
    DEFAULT_FILTERS.transactionTypeId
  );

  const [
    categoryId,
    setCategoryId,
  ] = useState(
    DEFAULT_FILTERS.categoryId
  );

  const [
    taxTreatmentId,
    setTaxTreatmentId,
  ] = useState(
    DEFAULT_FILTERS.taxTreatmentId
  );

  const [
    sortBy,
    setSortBy,
  ] = useState(
    DEFAULT_FILTERS.sortBy
  );

  const [
    search,
    setSearch,
  ] = useState(
    DEFAULT_FILTERS.search
  );

  const [
    searchDebounced,
    setSearchDebounced,
  ] = useState("");

  /*
   * ============================================================
   * SEARCH DEBOUNCE
   * ============================================================
   */

  useEffect(() => {
    const timer = setTimeout(
      () => {
        setSearchDebounced(
          search
        );
      },
      250
    );

    return () =>
      clearTimeout(
        timer
      );
  }, [search]);

  /*
   * ============================================================
   * FETCH TRANSACTIONS
   * ============================================================
   */

  const fetchAll =
    useCallback(
      async () => {
        try {
          setLoading(
            true
          );

          const rows =
            await db.listTransactions({
              from:
                from ||
                null,

              to:
                to ||
                null,

              transactionTypeId,

              categoryId,

              taxTreatmentId,

              sortBy,

              search:
                searchDebounced,
            });

          setTransactions(
            rows
          );
        } catch (error) {
          console.error(
            error
          );

          toast.error(
            "Failed to load transactions"
          );
        } finally {
          setLoading(
            false
          );
        }
      },
      [
        from,
        to,
        transactionTypeId,
        categoryId,
        taxTreatmentId,
        sortBy,
        searchDebounced,
      ]
    );

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /*
   * ============================================================
   * FETCH REFERENCE DATA
   * ============================================================
   */

  useEffect(() => {
    (async () => {
      try {
        const [
          transactionTypes,
          categoryRows,
          taxRows,
          eventRows,
        ] =
          await Promise.all([
            db.listTransactionTypes({
              includeArchived:
                true,
            }),

            db.listTransactionCategories({
              includeArchived:
                true,
            }),

            db.listTaxTreatmentStatuses({
              includeArchived:
                true,
            }),

            db.listEventsSummary({
              sortBy:
                "latest",
            }),
          ]);

        setTypes(
          transactionTypes
        );

        setCategories(
          categoryRows
        );

        setTaxTreatments(
          taxRows
        );

        setEvents(
          eventRows
        );
      } catch (error) {
        console.error(
          error
        );
      }
    })();
  }, []);

  /*
   * ============================================================
   * CATEGORY FILTER
   * ============================================================
   */

  useEffect(() => {
    if (
      categoryId ===
      "all"
    ) {
      return;
    }

    const category =
      categories.find(
        (item) =>
          item.id ===
          categoryId
      );

    if (
      category &&
      transactionTypeId !==
        "all" &&
      category.transactionTypeId !==
        transactionTypeId
    ) {
      setCategoryId(
        "all"
      );
    }
  }, [
    transactionTypeId,
    categoryId,
    categories,
  ]);

  const filteredCategoriesForFilter =
    useMemo(
      () => {
        if (
          transactionTypeId ===
          "all"
        ) {
          return categories;
        }

        return categories.filter(
          (item) =>
            item.transactionTypeId ===
            transactionTypeId
        );
      },
      [
        categories,
        transactionTypeId,
      ]
    );

  /*
   * ============================================================
   * LOOKUPS
   * ============================================================
   */

  const typeById = (
    id
  ) =>
    types.find(
      (item) =>
        item.id === id
    );

  const catById = (
    id
  ) =>
    categories.find(
      (item) =>
        item.id === id
    );

  const taxById = (
    id
  ) =>
    taxTreatments.find(
      (item) =>
        item.id === id
    );

  const evById = (
    id
  ) =>
    events.find(
      (item) =>
        item.id === id
    );

  /*
   * ============================================================
   * TOTAL
   * ============================================================
   */

  const totalAmount =
    useMemo(
      () =>
        transactions.reduce(
          (
            total,
            transaction
          ) =>
            total +
            Number(
              transaction.amount ||
                0
            ),
          0
        ),
      [
        transactions,
      ]
    );

  const filtersActive =
    from ||
    to ||
    transactionTypeId !==
      "all" ||
    categoryId !==
      "all" ||
    taxTreatmentId !==
      "all" ||
    sortBy !==
      "date_desc" ||
    search.trim();

  /*
   * ============================================================
   * FILTER ACTIONS
   * ============================================================
   */

  const clearAll = () => {
    setFrom("");
    setTo("");

    setTransactionTypeId(
      "all"
    );

    setCategoryId(
      "all"
    );

    setTaxTreatmentId(
      "all"
    );

    setSortBy(
      "date_desc"
    );

    setSearch("");
  };

  /*
   * ============================================================
   * ADD / EDIT / DUPLICATE
   * ============================================================
   */

  const openNew = () => {
    setEditing(
      null
    );

    setShowForm(
      true
    );
  };

  const openEdit = (
    transaction
  ) => {
    setEditing(
      transaction
    );

    setShowForm(
      true
    );
  };

  /*
   * DUPLICATE TRANSACTION
   *
   * We deliberately set id = null.
   *
   * TransactionForm treats this as a NEW transaction,
   * so saving it will create a brand-new transaction
   * instead of updating the original.
   *
   * We copy the useful bookkeeping details, including:
   *
   * - Transaction Type
   * - Category
   * - Amount
   * - Supplier / Customer
   * - Description
   * - Payment Method
   * - Tax Treatment
   * - Business Use %
   * - Quantity / Unit Cost
   * - Cash Flow Direction
   * - Notes
   * - Related Event
   *
   * We DO NOT copy:
   *
   * - Original transaction ID
   * - Receipt number
   * - Receipt attachments
   *
   * New date defaults to today.
   */

  const openDuplicate = (
    transaction
  ) => {
    setEditing({
      ...transaction,

      id:
        null,

      txDate:
        new Date()
          .toISOString()
          .slice(
            0,
            10
          ),

      receiptNumber:
        "",

      receiptStatus:
        "Missing",
    });

    setShowForm(
      true
    );
  };

  /*
   * ============================================================
   * VIEW TRANSACTION
   * ============================================================
   */

  const openView =
    async (
      transaction
    ) => {
      setViewTarget(
        transaction
      );

      setViewAttachments(
        []
      );

      setLoadingViewAttachments(
        true
      );

      try {
        const attachments =
          await db.listAttachments(
            "transaction",
            transaction.id
          );

        setViewAttachments(
          attachments ||
            []
        );
      } catch (error) {
        console.error(
          error
        );

        toast.error(
          "Could not load receipt attachments"
        );
      } finally {
        setLoadingViewAttachments(
          false
        );
      }
    };

  const closeView = () => {
    setViewTarget(
      null
    );

    setViewAttachments(
      []
    );

    setOpeningAttachmentId(
      null
    );
  };

  const openViewAttachment =
    async (
      attachment
    ) => {
      setOpeningAttachmentId(
        attachment.id
      );

      try {
        const url =
          await db.getAttachmentSignedUrl(
            attachment.filePath,
            3600
          );

        window.open(
          url,
          "_blank",
          "noopener,noreferrer"
        );
      } catch (error) {
        console.error(
          error
        );

        toast.error(
          "Could not open receipt"
        );
      } finally {
        setOpeningAttachmentId(
          null
        );
      }
    };

  /*
   * ============================================================
   * DATE PICKER
   * ============================================================
   */

  const openDatePicker = (
    event
  ) => {
    try {
      if (
        typeof event.currentTarget
          .showPicker ===
        "function"
      ) {
        event.currentTarget.showPicker();
      }
    } catch (error) {
      console.debug(
        "Native date picker fallback:",
        error
      );
    }
  };

  /*
   * ============================================================
   * DELETE FLOW
   * ============================================================
   */

  const openDelete =
    async (
      transaction
    ) => {
      setDeleteTarget(
        transaction
      );

      setLinkedDeleteAsset(
        null
      );

      const transactionType =
        typeById(
          transaction.transactionTypeId
        );

      if (
        transactionType?.plTreatment !==
        "asset_capitalized"
      ) {
        return;
      }

      setCheckingDeleteLink(
        true
      );

      try {
        const assets =
          await db.listAssets({
            statusId:
              "all",

            search:
              "",

            sortBy:
              "date_desc",
          });

        const linkedAsset =
          assets.find(
            (asset) =>
              asset.linkedTransactionId ===
              transaction.id
          ) ||
          null;

        setLinkedDeleteAsset(
          linkedAsset
        );
      } catch (error) {
        console.error(
          "Could not check linked asset:",
          error
        );

        toast.error(
          "Could not check whether this transaction is linked to an Asset. Please try again."
        );

        setDeleteTarget(
          null
        );

        setLinkedDeleteAsset(
          null
        );
      } finally {
        setCheckingDeleteLink(
          false
        );
      }
    };

  const closeDeleteDialog =
    () => {
      if (
        deleting
      ) {
        return;
      }

      setDeleteTarget(
        null
      );

      setLinkedDeleteAsset(
        null
      );

      setCheckingDeleteLink(
        false
      );
    };

  const confirmDelete =
    async () => {
      if (
        !deleteTarget ||
        deleting
      ) {
        return;
      }

      setDeleting(
        true
      );

      try {
        if (
          linkedDeleteAsset
        ) {
          await db.deleteAsset(
            linkedDeleteAsset.id
          );
        }

        await db.deleteTransaction(
          deleteTarget.id
        );

        if (
          linkedDeleteAsset
        ) {
          toast.success(
            `Transaction and linked asset "${linkedDeleteAsset.name}" deleted`
          );
        } else {
          toast.success(
            "Transaction deleted"
          );
        }

        setDeleteTarget(
          null
        );

        setLinkedDeleteAsset(
          null
        );

        await fetchAll();
      } catch (error) {
        console.error(
          error
        );

        toast.error(
          error.message ||
            "Failed to delete transaction"
        );
      } finally {
        setDeleting(
          false
        );
      }
    };

  /*
   * ============================================================
   * STYLES
   * ============================================================
   */

  const selectClass =
    "w-full bg-white border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-11 px-3 text-sm text-slate-800 appearance-none cursor-pointer transition-colors";

  const plBadge = (
    plTreatment
  ) => {
    const map = {
      operating_expense: {
        label:
          "Operating",

        cls:
          "bg-red-50 text-red-700 border-red-200",
      },

      product_purchase_cash_only: {
        label:
          "Purchase",

        cls:
          "bg-sky-50 text-sky-700 border-sky-200",
      },

      asset_capitalized: {
        label:
          "Asset",

        cls:
          "bg-violet-50 text-violet-700 border-violet-200",
      },

      owner_draw: {
        label:
          "Owner Draw",

        cls:
          "bg-amber-50 text-amber-800 border-amber-200",
      },

      prepaid_event_rental: {
        label:
          "Prepaid Rental",

        cls:
          "bg-blue-50 text-blue-700 border-blue-200",
      },

      pending: {
        label:
          "Pending",

        cls:
          "bg-slate-100 text-slate-600 border-slate-200",
      },
    };

    const badge =
      map[
        plTreatment
      ] ||
      map.pending;

    return (
      <span
        className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${badge.cls}`}
      >
        {
          badge.label
        }
      </span>
    );
  };

  /*
   * ============================================================
   * VIEW TARGET LOOKUPS
   * ============================================================
   */

  const viewType =
    viewTarget
      ? typeById(
          viewTarget.transactionTypeId
        )
      : null;

  const viewCategory =
    viewTarget
      ? catById(
          viewTarget.categoryId
        )
      : null;

  const viewTax =
    viewTarget
      ? taxById(
          viewTarget.taxTreatmentId
        )
      : null;

  const viewEvent =
    viewTarget
      ? evById(
          viewTarget.relatedEventId
        )
      : null;

  /*
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24">

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 space-y-6">

        {/* HEADER */}

        <div className="flex items-start justify-between gap-4 flex-wrap">

          <div>

            <h1
              className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#193f7d]"
              style={{
                fontFamily:
                  "Outfit, sans-serif",
              }}
              data-testid="transactions-title"
            >
              Transactions
            </h1>

            <p className="text-sm text-slate-600 mt-1">
              Unified bookkeeping ledger
            </p>

          </div>

          <Button
            onClick={
              openNew
            }
            className="bg-[#fa9d1c] hover:bg-[#e58a10] text-white rounded-full font-semibold h-11 px-5 transition-transform active:scale-95"
            data-testid="add-transaction-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Transaction
          </Button>

        </div>

        {/* EXPORT */}

        <ExportBar
          testIdPrefix="tx-export"
          buildReport={() => {
            const columns = [
              {
                key:
                  "txDate",

                label:
                  "Date",
              },

              {
                key:
                  "type",

                label:
                  "Type",
              },

              {
                key:
                  "category",

                label:
                  "Category",
              },

              {
                key:
                  "description",

                label:
                  "Description",
              },

              {
                key:
                  "supplierCustomer",

                label:
                  "Supplier / Customer",
              },

              {
                key:
                  "amount",

                label:
                  "Amount (RM)",

                type:
                  "currency",

                align:
                  "right",
              },

              {
                key:
                  "paymentMethod",

                label:
                  "Payment Method",
              },

              {
                key:
                  "receiptStatus",

                label:
                  "Receipt",
              },

              {
                key:
                  "receiptNumber",

                label:
                  "Receipt No.",
              },

              {
                key:
                  "notes",

                label:
                  "Notes",
              },
            ];

            const rows =
              transactions.map(
                (
                  transaction
                ) => ({
                  ...transaction,

                  type:
                    typeById(
                      transaction.transactionTypeId
                    )?.name ||
                    "",

                  category:
                    catById(
                      transaction.categoryId
                    )?.name ||
                    "",
                })
              );

            const total = {
              txDate:
                "TOTAL",

              amount:
                rows.reduce(
                  (
                    sum,
                    row
                  ) =>
                    sum +
                    Number(
                      row.amount ||
                        0
                    ),
                  0
                ),
            };

            const period =
              from ||
              to
                ? `${from || "…"} → ${
                    to || "…"
                  }`
                : "All periods";

            return {
              title:
                "Transactions",

              subtitle:
                period,

              filename:
                `Gao-Chill-Transactions-${
                  from ||
                  "all"
                }${
                  to
                    ? `_to_${to}`
                    : ""
                }`,

              columns,

              rows,

              totals:
                total,

              sections: [
                {
                  heading:
                    "Ledger",

                  columns,

                  rows,

                  totals:
                    total,
                },
              ],

              metrics: [
                {
                  label:
                    "Records",

                  value:
                    transactions.length,
                },

                {
                  label:
                    "Total Amount",

                  value:
                    formatCurrency(
                      total.amount
                    ),
                },
              ],

              footnote:
                "Amounts in Malaysian Ringgit (MYR). Generated by Gao Chill 五告凉 bookkeeping.",
            };
          }}
        />

        {/* FILTERS */}

        <section
          className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm space-y-4"
          data-testid="tx-filters"
        >

          <div className="relative">

            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />

            <input
              type="text"
              value={
                search
              }
              onChange={(
                event
              ) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search description, supplier, receipt number, notes…"
              className="w-full bg-white border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-11 pl-9 pr-9 text-sm text-slate-800 transition-colors"
              data-testid="tx-filter-search"
            />

            {search && (
              <button
                type="button"
                onClick={() =>
                  setSearch("")
                }
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1 rounded-full"
                data-testid="tx-filter-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}

          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">

            <div>

              <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block">
                From Date
              </label>

              <input
                type="date"
                value={
                  from
                }
                onChange={(
                  event
                ) =>
                  setFrom(
                    event.target.value
                  )
                }
                onClick={
                  openDatePicker
                }
                className={
                  selectClass
                }
                data-testid="tx-filter-from"
              />

            </div>

            <div>

              <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block">
                To Date
              </label>

              <input
                type="date"
                value={
                  to
                }
                onChange={(
                  event
                ) =>
                  setTo(
                    event.target.value
                  )
                }
                onClick={
                  openDatePicker
                }
                className={
                  selectClass
                }
                data-testid="tx-filter-to"
              />

            </div>

            <div>

              <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block">
                Sort By
              </label>

              <select
                value={
                  sortBy
                }
                onChange={(
                  event
                ) =>
                  setSortBy(
                    event.target.value
                  )
                }
                className={
                  selectClass
                }
                data-testid="tx-filter-sort"
              >

                {SORT_OPTIONS.map(
                  (
                    option
                  ) => (
                    <option
                      key={
                        option.value
                      }
                      value={
                        option.value
                      }
                    >
                      {
                        option.label
                      }
                    </option>
                  )
                )}

              </select>

            </div>

            <div>

              <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block">
                Transaction Type
              </label>

              <select
                value={
                  transactionTypeId
                }
                onChange={(
                  event
                ) =>
                  setTransactionTypeId(
                    event.target.value
                  )
                }
                className={
                  selectClass
                }
                data-testid="tx-filter-type"
              >

                <option value="all">
                  All Types
                </option>

                {types.map(
                  (
                    type
                  ) => (
                    <option
                      key={
                        type.id
                      }
                      value={
                        type.id
                      }
                    >
                      {
                        type.name
                      }

                      {type.isArchived
                        ? " (archived)"
                        : ""}
                    </option>
                  )
                )}

              </select>

            </div>

            <div>

              <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block">
                Category
              </label>

              <select
                value={
                  categoryId
                }
                onChange={(
                  event
                ) =>
                  setCategoryId(
                    event.target.value
                  )
                }
                className={
                  selectClass
                }
                data-testid="tx-filter-category"
              >

                <option value="all">
                  All Categories
                </option>

                {filteredCategoriesForFilter.map(
                  (
                    category
                  ) => (
                    <option
                      key={
                        category.id
                      }
                      value={
                        category.id
                      }
                    >
                      {
                        category.name
                      }

                      {category.isArchived
                        ? " (archived)"
                        : ""}
                    </option>
                  )
                )}

              </select>

            </div>

            <div>

              <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block">
                Tax Treatment
              </label>

              <select
                value={
                  taxTreatmentId
                }
                onChange={(
                  event
                ) =>
                  setTaxTreatmentId(
                    event.target.value
                  )
                }
                className={
                  selectClass
                }
                data-testid="tx-filter-tax"
              >

                <option value="all">
                  All Treatments
                </option>

                {taxTreatments.map(
                  (
                    taxTreatment
                  ) => (
                    <option
                      key={
                        taxTreatment.id
                      }
                      value={
                        taxTreatment.id
                      }
                    >
                      {
                        taxTreatment.name
                      }

                      {taxTreatment.isArchived
                        ? " (archived)"
                        : ""}
                    </option>
                  )
                )}

              </select>

            </div>

          </div>

          {filtersActive && (
            <div className="flex justify-end">

              <button
                type="button"
                onClick={
                  clearAll
                }
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-[#193f7d] hover:bg-slate-50 text-xs font-semibold text-slate-600 hover:text-[#193f7d] transition-colors"
                data-testid="tx-filter-reset"
              >
                <X className="w-3.5 h-3.5" />
                Reset Filters
              </button>

            </div>
          )}

        </section>

        {/* SUMMARY */}

        <div
          className="bg-gradient-to-br from-[#193f7d] to-[#122f5e] text-white rounded-2xl p-4 sm:p-5 shadow-lg"
          data-testid="tx-summary-strip"
        >

          <div className="grid grid-cols-2 gap-4">

            <div>

              <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">
                Records shown
              </p>

              <p
                className="text-2xl font-mono font-bold mt-1"
                data-testid="tx-summary-count"
              >
                {
                  transactions.length
                }
              </p>

            </div>

            <div className="text-right">

              <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">
                Total amount
              </p>

              <p
                className="text-2xl font-mono font-bold mt-1"
                data-testid="tx-summary-total"
              >
                {formatCurrency(
                  totalAmount
                )}
              </p>

            </div>

          </div>

        </div>

        {/* TRANSACTION LIST */}

        {loading ? (
          <div
            className="text-center py-12 text-slate-500"
            data-testid="tx-loading"
          >
            Loading transactions…
          </div>
        ) : transactions.length ===
          0 ? (
          <div
            className="text-center py-16 bg-white border border-slate-200 rounded-2xl"
            data-testid="tx-empty"
          >

            <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />

            <p className="text-slate-600 font-medium">
              {filtersActive
                ? "No transactions match your filters."
                : "No transactions yet"}
            </p>

            <p className="text-sm text-slate-400 mt-2">
              {filtersActive
                ? "Try adjusting or resetting the filters."
                : "Click 'Add Transaction' to log your first entry."}
            </p>

          </div>
        ) : (
          <div className="space-y-3">

            {transactions.map(
              (
                transaction
              ) => {
                const transactionType =
                  typeById(
                    transaction.transactionTypeId
                  );

                const category =
                  catById(
                    transaction.categoryId
                  );

                const taxTreatment =
                  taxById(
                    transaction.taxTreatmentId
                  );

                const relatedEvent =
                  evById(
                    transaction.relatedEventId
                  );

                return (
                  <div
                    key={
                      transaction.id
                    }
                    className="bg-white border border-slate-200 hover:border-[#193f7d]/40 rounded-2xl p-4 sm:p-5 shadow-sm transition-colors"
                    data-testid={`tx-row-${transaction.id}`}
                  >

                    <div className="flex items-start justify-between gap-3">

                      <div className="min-w-0 flex-1">

                        <div className="flex items-center gap-2 flex-wrap">

                          <span className="text-xs font-mono font-semibold text-slate-500">
                            {
                              transaction.txDate
                            }
                          </span>

                          {transactionType &&
                            plBadge(
                              transactionType.plTreatment
                            )}

                          {transaction.receiptStatus ===
                            "Missing" && (
                            <span
                              className="text-[10px] font-bold uppercase tracking-wider bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5"
                              data-testid={`tx-receipt-missing-${transaction.id}`}
                            >
                              Receipt Missing
                            </span>
                          )}

                          {transaction.receiptStatus ===
                            "Attached" && (
                            <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                              Receipt Attached
                            </span>
                          )}

                        </div>

                        <p
                          className="text-base font-semibold text-slate-900 mt-1 truncate"
                          data-testid={`tx-description-${transaction.id}`}
                        >
                          {transaction.description || (
                            <span className="text-slate-400 italic">
                              No description
                            </span>
                          )}
                        </p>

                        <div className="flex items-center gap-2 text-xs text-slate-600 mt-1 flex-wrap">

                          <span className="font-semibold text-[#193f7d]">
                            {transactionType?.name ||
                              "—"}
                          </span>

                          <span className="text-slate-300">
                            ·
                          </span>

                          <span>
                            {category?.name ||
                              "—"}
                          </span>

                          {transaction.supplierCustomer && (
                            <>

                              <span className="text-slate-300">
                                ·
                              </span>

                              <span>
                                {
                                  transaction.supplierCustomer
                                }
                              </span>

                            </>
                          )}

                          {relatedEvent && (
                            <>

                              <span className="text-slate-300">
                                ·
                              </span>

                              <span className="italic">
                                Linked:{" "}
                                {
                                  relatedEvent.eventName
                                }
                              </span>

                            </>
                          )}

                        </div>

                        {taxTreatment && (
                          <p className="text-[11px] text-slate-500 mt-1">
                            Tax treatment:{" "}
                            <span className="font-semibold">
                              {
                                taxTreatment.name
                              }
                            </span>
                          </p>
                        )}

                      </div>

                      <div className="flex-shrink-0 text-right">

                        <p
                          className="text-xl font-mono font-bold text-slate-900"
                          data-testid={`tx-amount-${transaction.id}`}
                        >
                          {formatCurrency(
                            transaction.amount
                          )}
                        </p>

                        {transaction.paymentMethod && (
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">
                            {
                              transaction.paymentMethod
                            }
                          </p>
                        )}

                      </div>

                    </div>

                    {/* ACTION BUTTONS */}

                    <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-slate-100 flex-wrap">

                      <button
                        type="button"
                        onClick={() =>
                          openView(
                            transaction
                          )
                        }
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-[#193f7d] hover:bg-blue-50 text-xs font-semibold text-slate-600 hover:text-[#193f7d] transition-colors"
                        data-testid={`tx-view-${transaction.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        View
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          openEdit(
                            transaction
                          )
                        }
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-[#193f7d] hover:bg-slate-50 text-xs font-semibold text-slate-600 hover:text-[#193f7d] transition-colors"
                        data-testid={`tx-edit-${transaction.id}`}
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          openDuplicate(
                            transaction
                          )
                        }
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50 text-xs font-semibold text-slate-600 hover:text-[#193f7d] transition-colors"
                        data-testid={`tx-duplicate-${transaction.id}`}
                      >
                        <Copy className="w-3.5 h-3.5" />
                        Duplicate
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          openDelete(
                            transaction
                          )
                        }
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-red-300 hover:bg-red-50 text-xs font-semibold text-slate-600 hover:text-red-700 transition-colors"
                        data-testid={`tx-delete-${transaction.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete
                      </button>

                    </div>

                  </div>
                );
              }
            )}

          </div>
        )}

        {/* NOTICE */}

        <div
          className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 leading-relaxed"
          data-testid="tx-notice"
        >

          <FileText className="w-4 h-4 inline-block mr-1.5 text-slate-500 align-text-bottom" />

          <strong>
            Reminder:
          </strong>{" "}

          Product / Stock Purchases entered here reduce Monthly P&amp;L.
          Event-level Product Cost remains in each Per Event P&amp;L for
          internal event profitability analysis and is not deducted again
          in Monthly P&amp;L. Owner Withdrawals do not reduce Monthly Net
          Profit; they only reduce Profit Retained. Equipment / Asset
          Purchases are capitalised and do not reduce Monthly Net Profit.

        </div>

      </div>

      {/* =======================================================
          ADD / EDIT / DUPLICATE FORM
      ======================================================== */}

      <TransactionForm
        open={
          showForm
        }
        onClose={() => {
          setShowForm(
            false
          );

          setEditing(
            null
          );
        }}
        onSaved={
          fetchAll
        }
        initial={
          editing
        }
        types={
          types
        }
        categories={
          categories
        }
        taxTreatments={
          taxTreatments
        }
        events={
          events
        }
      />

      {/* =======================================================
          VIEW TRANSACTION MODAL
      ======================================================== */}

      {viewTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onMouseDown={
            closeView
          }
          data-testid="tx-view-modal"
        >

          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onMouseDown={(
              event
            ) =>
              event.stopPropagation()
            }
          >

            {/* MODAL HEADER */}

            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 sm:px-6 py-4 flex items-start justify-between gap-4 rounded-t-2xl">

              <div>

                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">
                  Transaction Details
                </p>

                <h2
                  className="text-xl font-semibold text-[#193f7d] mt-1"
                  style={{
                    fontFamily:
                      "Outfit, sans-serif",
                  }}
                >
                  {viewTarget.description ||
                    "Transaction"}
                </h2>

              </div>

              <button
                type="button"
                onClick={
                  closeView
                }
                className="p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

            </div>

            <div className="p-5 sm:p-6 space-y-6">

              {/* AMOUNT */}

              <div className="bg-gradient-to-br from-[#193f7d] to-[#122f5e] rounded-2xl p-4 text-white">

                <div className="flex items-start justify-between gap-4">

                  <div>

                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">
                      Amount
                    </p>

                    <p className="text-2xl sm:text-3xl font-mono font-bold mt-1">
                      {formatCurrency(
                        viewTarget.amount
                      )}
                    </p>

                  </div>

                  <div className="text-right">

                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">
                      Date
                    </p>

                    <p className="text-sm font-semibold mt-1">
                      {
                        viewTarget.txDate
                      }
                    </p>

                  </div>

                </div>

              </div>

              {/* BASIC DETAILS */}

              <div>

                <SectionTitle>
                  Transaction Summary
                </SectionTitle>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 mt-3">

                  <ViewField
                    label="Transaction Type"
                    value={
                      viewType?.name ||
                      "—"
                    }
                  />

                  <ViewField
                    label="Category"
                    value={
                      viewCategory?.name ||
                      "—"
                    }
                  />

                  <ViewField
                    label="Supplier / Customer"
                    value={
                      viewTarget.supplierCustomer ||
                      "—"
                    }
                  />

                  <ViewField
                    label="Payment Method"
                    value={
                      viewTarget.paymentMethod ||
                      "—"
                    }
                  />

                  <ViewField
                    label="Receipt Number"
                    value={
                      viewTarget.receiptNumber ||
                      "—"
                    }
                  />

                  <ViewField
                    label="Receipt Status"
                    value={
                      viewTarget.receiptStatus ||
                      "—"
                    }
                  />

                  <ViewField
                    label="Tax Treatment"
                    value={
                      viewTax?.name ||
                      "—"
                    }
                  />

                  <ViewField
                    label="P&L Treatment"
                    value={
                      viewType?.plTreatment
                        ? viewType.plTreatment
                            .replace(
                              /_/g,
                              " "
                            )
                        : "—"
                    }
                  />

                  {viewEvent && (
                    <ViewField
                      label="Related Event"
                      value={
                        viewEvent.eventName
                      }
                    />
                  )}

                  {viewTarget.businessUsePercentage !==
                    undefined &&
                    viewTarget.businessUsePercentage !==
                      null &&
                    viewTarget.businessUsePercentage !==
                      "" && (
                      <ViewField
                        label="Business Use"
                        value={`${viewTarget.businessUsePercentage}%`}
                      />
                    )}

                </div>

              </div>

              {/* DESCRIPTION */}

              <div>

                <SectionTitle>
                  Description
                </SectionTitle>

                <div className="mt-3 bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-slate-700 whitespace-pre-wrap">
                  {viewTarget.description ||
                    "No description"}
                </div>

              </div>

              {/* NOTES */}

              {viewTarget.notes && (
                <div>

                  <SectionTitle>
                    Notes
                  </SectionTitle>

                  <div className="mt-3 bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-slate-700 whitespace-pre-wrap">
                    {
                      viewTarget.notes
                    }
                  </div>

                </div>
              )}

              {/* RECEIPTS */}

              <div>

                <div className="flex items-center justify-between gap-3">

                  <SectionTitle>
                    Receipt Attachments
                  </SectionTitle>

                  {!loadingViewAttachments && (
                    <span className="text-xs text-slate-400">
                      {
                        viewAttachments.length
                      }{" "}
                      file
                      {viewAttachments.length ===
                      1
                        ? ""
                        : "s"}
                    </span>
                  )}

                </div>

                <div className="mt-3">

                  {loadingViewAttachments ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500 py-4">

                      <Loader2 className="w-4 h-4 animate-spin" />

                      Loading receipts…

                    </div>
                  ) : viewAttachments.length ===
                    0 ? (
                    <div className="border-2 border-dashed border-slate-200 rounded-xl p-5 text-center">

                      <Receipt className="w-8 h-8 text-slate-300 mx-auto mb-2" />

                      <p className="text-sm text-slate-500">
                        No receipt attachment found.
                      </p>

                    </div>
                  ) : (
                    <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">

                      {viewAttachments.map(
                        (
                          attachment,
                          index
                        ) => (
                          <div
                            key={
                              attachment.id
                            }
                            className="flex items-center gap-3 p-3 bg-white"
                          >

                            <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">

                              <Paperclip className="w-4 h-4 text-slate-500" />

                            </div>

                            <div className="min-w-0 flex-1">

                              <p className="text-sm font-semibold text-slate-800 truncate">
                                {attachment.fileName ||
                                  `Receipt ${
                                    index +
                                    1
                                  }`}
                              </p>

                              <p className="text-[11px] text-slate-400 mt-0.5">

                                {attachment.fileType ||
                                  "Attachment"}

                                {attachment.fileSize
                                  ? ` · ${(
                                      attachment.fileSize /
                                      1024
                                    ).toFixed(
                                      0
                                    )} KB`
                                  : ""}

                                {attachment.uploadedAt
                                  ? ` · ${attachment.uploadedAt.slice(
                                      0,
                                      10
                                    )}`
                                  : ""}

                              </p>

                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                openViewAttachment(
                                  attachment
                                )
                              }
                              disabled={
                                openingAttachmentId ===
                                attachment.id
                              }
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-[#193f7d] hover:bg-blue-50 text-xs font-semibold text-slate-600 hover:text-[#193f7d] disabled:opacity-60 transition-colors"
                            >

                              {openingAttachmentId ===
                              attachment.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <ExternalLink className="w-3.5 h-3.5" />
                              )}

                              Open Receipt

                            </button>

                          </div>
                        )
                      )}

                    </div>
                  )}

                </div>

              </div>

              {/* VIEW MODAL FOOTER */}

              <div className="border-t border-slate-200 pt-4 flex justify-end">

                <Button
                  type="button"
                  onClick={
                    closeView
                  }
                  variant="outline"
                  className="rounded-full px-5"
                >
                  Close
                </Button>

              </div>

            </div>

          </div>

        </div>
      )}

      {/* =======================================================
          DELETE CONFIRMATION
      ======================================================== */}

      <AlertDialog
        open={
          !!deleteTarget
        }
        onOpenChange={(
          open
        ) => {
          if (
            !open
          ) {
            closeDeleteDialog();
          }
        }}
      >

        <AlertDialogContent>

          <AlertDialogHeader>

            <AlertDialogTitle>
              {linkedDeleteAsset
                ? "Delete transaction and linked asset?"
                : "Delete this transaction?"}
            </AlertDialogTitle>

            <AlertDialogDescription asChild>

              <div className="space-y-3 text-sm">

                {checkingDeleteLink ? (
                  <p>
                    Checking whether this transaction is linked to an Asset Register item…
                  </p>
                ) : linkedDeleteAsset ? (
                  <>

                    <p>
                      This Equipment / Asset Purchase transaction is linked to:
                    </p>

                    <div className="rounded-xl border border-violet-200 bg-violet-50 p-3">

                      <p className="font-semibold text-violet-900">
                        {
                          linkedDeleteAsset.name
                        }
                      </p>

                      <p className="text-violet-700 mt-1">

                        {formatCurrency(
                          linkedDeleteAsset.purchaseAmount
                        )}

                        {linkedDeleteAsset.purchaseDate
                          ? ` · ${linkedDeleteAsset.purchaseDate}`
                          : ""}

                      </p>

                    </div>

                    <p>
                      Confirming this deletion will remove both the Transaction and the linked Asset Register entry.
                    </p>

                    <p className="font-semibold text-red-700">
                      This action cannot be undone.
                    </p>

                  </>
                ) : (
                  <>

                    <p>
                      This action cannot be undone. The transaction will be removed from all reports.
                    </p>

                    {typeById(
                      deleteTarget?.transactionTypeId
                    )?.plTreatment ===
                      "asset_capitalized" && (
                      <p>
                        No linked Asset Register item was found for this transaction, so only the transaction will be deleted.
                      </p>
                    )}

                  </>
                )}

              </div>

            </AlertDialogDescription>

          </AlertDialogHeader>

          <AlertDialogFooter>

            <AlertDialogCancel
              disabled={
                deleting
              }
              data-testid="tx-delete-cancel"
            >
              Cancel
            </AlertDialogCancel>

            <AlertDialogAction
              onClick={
                confirmDelete
              }
              disabled={
                deleting ||
                checkingDeleteLink
              }
              className="bg-red-600 hover:bg-red-700"
              data-testid="tx-delete-confirm"
            >
              {deleting
                ? "Deleting…"
                : linkedDeleteAsset
                ? "Delete Both"
                : "Delete"}
            </AlertDialogAction>

          </AlertDialogFooter>

        </AlertDialogContent>

      </AlertDialog>

    </div>
  );
};

/*
 * ============================================================
 * VIEW MODAL HELPERS
 * ============================================================
 */

const SectionTitle = ({
  children,
}) => (
  <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
    {children}
  </h3>
);

const ViewField = ({
  label,
  value,
}) => (
  <div>

    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
      {label}
    </p>

    <p className="text-sm font-medium text-slate-800 mt-1 break-words">
      {value}
    </p>

  </div>
);

export default Transactions;
